/**
 * PLT-5741: passive per-message feedback control for the `/chat` assistant.
 *
 * Renders under every assistant reply, next to the copy button:
 *
 *     [copy] [👍] [👎]   ← boxed, bordered icon buttons
 *
 * Behaviour (locked in with Janne on the PLT-5741 thread):
 *   - Passive + always-visible-at-rest: the icons sit at ~40% opacity and go
 *     full-opacity on hover / focus / when voted. No desktop hover-reveal —
 *     touch devices can't hover and the muted icons don't compete with the
 *     answer. (The CSS lives in upright-chat-theme.css.)
 *   - 👍 submits silently and fills the thumb. 👎 submits the negative signal
 *     immediately (fills the thumb) AND opens an optional reason popover with
 *     preset chips + a free-text line; closing it without a chip leaves a bare
 *     👎 recorded. Re-voting is reversible: clicking an active thumb clears it.
 *   - State is optimistic — the thumb fills on click and reverts if the POST
 *     fails. It is seeded from `metadata.custom.feedback` so a reopened / reloaded
 *     thread shows the persisted vote (see seed-message.ts + conversations.ts).
 *   - Instrumentation: an impression fires once when the control first scrolls
 *     into view, a vote event on every up/down/clear, and a reason-submit event
 *     when the reason popover is confirmed — so we can watch impressions vs.
 *     clicks from day one.
 *   - PLT-7846: the newest reply's row is drawn at full strength with a "Was this
 *     helpful?" label in front of the thumbs; older replies keep the muted row, so
 *     a long conversation does not repeat the question under every answer. The
 *     thumbs had no external votes in their first two months at 40% everywhere.
 *   - PLT-7846: for organisations that can ask Upright to review a conversation,
 *     the newest reply's reason popover ends with a link to do so. A rating stays one click and
 *     reaches no one in particular; the link is the separate, deliberate step that puts the
 *     conversation in front of the Upright team. It asks
 *     the assistant in the user's name, which answers with the review card.
 *
 * The buttons are plain `<button>` elements (not the design-system `IconButton`
 * / `Button`, whose chrome className and fixed palette we don't want here) so the
 * feedback CSS in upright-chat-theme.css can render them as the bordered,
 * rounded-square icon buttons the design calls for — muted at rest, with the
 * active vote filling brand lilac. The Submit button in the reason popover is a
 * plain button for the same reason (the design system has no lilac button
 * colour). Tooltips are the design-system react-aria `Tooltip`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionBarPrimitive, useAssistantRuntime, useMessage, useThread } from '@assistant-ui/react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Text } from '@upright/ui/common/generic/components/typography';
import { composerState } from '../upright-chat-shared';
import { reviewRequestAvailabilityQuery } from '../use-review-request';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip/Tooltip';
import { PopoverTooltip } from '@upright/ui/common/generic/components/tooltip/PopoverTooltip';
import { Heading } from '@upright/ui/common/generic/components/typography/Heading';
import {
  CHAT_FEEDBACK_REASON_CHIPS,
  CHAT_FEEDBACK_REASON_TEXT_MAX_LENGTH,
  type ChatFeedbackRating,
} from '@upright/common/chat/feedback-types';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { submitChatFeedback } from './chat-feedback-api';
import {
  CheckIcon,
  CloseIcon,
  CopyIcon,
  ThumbsDownFilledIcon,
  ThumbsDownIcon,
  ThumbsUpFilledIcon,
  ThumbsUpIcon,
} from './chat-feedback-icons';

const TOOLTIP_OPEN_DELAY_MS = 400;
/** Server-side answer/question snapshots are capped at 4000; trim client-side too. */
const SNAPSHOT_MAX_LENGTH = 4000;

interface SeededFeedback {
  rating: ChatFeedbackRating;
  reasonChips: string[];
  reasonText: string | null;
}

/** The coordinates + seed the adapter / reload path stamp on assistant turns. */
interface FeedbackCustomMetadata {
  threadId?: unknown;
  messageIndex?: unknown;
  feedback?: unknown;
}

/**
 * Pull the `(threadId, messageIndex)` this reply is keyed by, plus any persisted
 * vote, from the message metadata. Defensive: `metadata.custom` is a loosely
 * typed `Record<string, unknown>`, so every field is validated before use.
 */
function readFeedbackMetadata(custom: Record<string, unknown> | undefined): {
  threadId: string | null;
  messageIndex: number | null;
  seed: SeededFeedback | null;
} {
  const meta = (custom ?? {}) as FeedbackCustomMetadata;
  const threadId = typeof meta.threadId === 'string' ? meta.threadId : null;
  const messageIndex = typeof meta.messageIndex === 'number' ? meta.messageIndex : null;

  let seed: SeededFeedback | null = null;
  const fb = meta.feedback;
  if (fb && typeof fb === 'object') {
    const rating = (fb as { rating?: unknown }).rating;
    if (rating === 'positive' || rating === 'negative') {
      const chipsRaw = (fb as { reasonChips?: unknown }).reasonChips;
      const textRaw = (fb as { reasonText?: unknown }).reasonText;
      seed = {
        rating,
        reasonChips: Array.isArray(chipsRaw) ? chipsRaw.filter((c): c is string => typeof c === 'string') : [],
        reasonText: typeof textRaw === 'string' ? textRaw : null,
      };
    }
  }
  return { threadId, messageIndex, seed };
}

/** Flatten the visible text parts of a message into a single trimmed snapshot. */
function extractText(content: ReadonlyArray<{ type: string; text?: string }> | undefined, max: number): string | null {
  if (!content) return null;
  const text = content
    .filter(p => p.type === 'text' && typeof p.text === 'string')
    .map(p => p.text)
    .join('\n')
    .trim();
  if (text.length === 0) return null;
  return text.length <= max ? text : text.slice(0, max);
}

export function ChatFeedbackControl() {
  const pushGtmEvent = usePushGtmEvent();

  // Coordinates + persisted vote for this assistant turn.
  const custom = useMessage(m => m.metadata.custom);
  const { threadId, messageIndex, seed } = useMemo(() => readFeedbackMetadata(custom), [custom]);

  // Best-effort snapshots for the Slack card: this reply's text and the user
  // question that prompted it (the user turn immediately before this one).
  const answerText = useMessage(m => extractText(m.content, SNAPSHOT_MAX_LENGTH));
  const messagePosition = useMessage(m => m.index);
  const userQuestion = useThread(t => {
    // Load-bearing twice: there is no prior turn for the first message, and
    // `.at()` would read backwards from the end on a negative index.
    if (messagePosition <= 0) return null;
    // `.at()` over `t.messages[i]`: `messagePosition` comes from a different
    // snapshot than `t.messages`, so a thread switch can leave it past the new
    // thread's end. The `T | undefined` return is also what keeps the runtime
    // guard below from tripping no-unnecessary-condition.
    const prior = t.messages.at(messagePosition - 1);
    if (!prior || prior.role !== 'user') return null;
    return extractText(prior.content, SNAPSHOT_MAX_LENGTH);
  });
  const isLast = useMessage(m => m.isLast);
  // Hide the control while THIS reply is still streaming — a half-written answer
  // isn't rateable yet. Only the last message can be running, so gate on both.
  const isRunning = useThread(t => t.isRunning);

  // Optimistic vote state, seeded once from the persisted feedback. The server
  // is the source of truth on reload; between reloads this local state owns the
  // filled/empty rendering and reverts on a failed POST.
  const [rating, setRating] = useState<ChatFeedbackRating | null>(seed?.rating ?? null);
  const [selectedChips, setSelectedChips] = useState<string[]>(seed?.reasonChips ?? []);
  const [reasonText, setReasonText] = useState<string>(seed?.reasonText ?? '');
  const [reasonOpen, setReasonOpen] = useState(false);

  const thumbsDownRef = useRef<HTMLButtonElement>(null);

  const canSubmit = threadId !== null && messageIndex !== null;

  // Fire-and-forget POST with optimistic revert. `next` is the vote we're
  // moving to; `prev` is captured so a failure restores exactly what was shown.
  const persist = useCallback(
    (
      next: ChatFeedbackRating | null,
      chips: string[],
      text: string,
      prev: { rating: ChatFeedbackRating | null; chips: string[]; text: string },
    ) => {
      if (!canSubmit) return;
      void submitChatFeedback({
        conversationId: threadId,
        messageIndex,
        rating: next,
        reasonChips: chips,
        reasonText: text.trim().length > 0 ? text.trim() : null,
        userQuestion,
        answerText,
      }).catch(() => {
        // Revert the optimistic vote state so the UI matches the server again.
        // Deliberately do NOT touch `reasonOpen`: the reason popover is opened
        // and closed by user intent (click 👎 / Submit / click-away / Esc), never
        // by the outcome of this fire-and-forget POST. Coupling them made the
        // popover flash open then vanish whenever the vote POST failed (e.g. a
        // frontend-only preview whose backend lacks the feedback endpoint).
        setRating(prev.rating);
        setSelectedChips(prev.chips);
        setReasonText(prev.text);
      });
    },
    [canSubmit, threadId, messageIndex, userQuestion, answerText],
  );

  const handlePositive = useCallback(() => {
    const prev = { rating, chips: selectedChips, text: reasonText };
    if (rating === 'positive') {
      // Toggle off → clear the vote.
      setRating(null);
      persist(null, [], '', prev);
      pushGtmEvent({ event: CustomGtmEvent.CHAT_FEEDBACK_VOTE, data: { rating: 'cleared' } });
      return;
    }
    // Switching from 👎 (or unvoted) to 👍 drops any reason and closes the popover.
    setRating('positive');
    setSelectedChips([]);
    setReasonText('');
    setReasonOpen(false);
    persist('positive', [], '', prev);
    pushGtmEvent({ event: CustomGtmEvent.CHAT_FEEDBACK_VOTE, data: { rating: 'positive' } });
  }, [rating, selectedChips, reasonText, persist, pushGtmEvent]);

  const handleNegative = useCallback(() => {
    const prev = { rating, chips: selectedChips, text: reasonText };
    if (rating === 'negative') {
      // Toggle off → clear the vote and close the reason popover.
      setRating(null);
      setSelectedChips([]);
      setReasonText('');
      setReasonOpen(false);
      persist(null, [], '', prev);
      pushGtmEvent({ event: CustomGtmEvent.CHAT_FEEDBACK_VOTE, data: { rating: 'cleared' } });
      return;
    }
    // Submit the bare negative signal immediately, then reveal the optional
    // reason popover. Closing it without a chip leaves the bare 👎 recorded.
    setRating('negative');
    setSelectedChips([]);
    setReasonText('');
    persist('negative', [], '', prev);
    pushGtmEvent({ event: CustomGtmEvent.CHAT_FEEDBACK_VOTE, data: { rating: 'negative' } });
    setReasonOpen(true);
  }, [rating, selectedChips, reasonText, persist, pushGtmEvent]);

  const toggleChip = useCallback((id: string) => {
    setSelectedChips(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]));
  }, []);

  // Persist the reason (chips + free text) on top of the existing 👎.
  const submitReason = useCallback(() => {
    if (rating !== 'negative') return;
    const prev = { rating, chips: selectedChips, text: reasonText };
    persist('negative', selectedChips, reasonText, prev);
    pushGtmEvent({
      event: CustomGtmEvent.CHAT_FEEDBACK_REASON_SUBMIT,
      data: { reasonChipCount: selectedChips.length, hasReasonText: reasonText.trim().length > 0 },
    });
    setReasonOpen(false);
  }, [rating, selectedChips, reasonText, persist, pushGtmEvent]);

  // Closing the popover any other way (click-away / Esc) still keeps whatever
  // reason was already persisted; the bare 👎 stands on its own.
  const onReasonOpenChange = useCallback(
    (open: boolean) => {
      setReasonOpen(open);
      if (!open && rating === 'negative') {
        // Persist any chips/text picked before dismissing without the button.
        if (selectedChips.length > 0 || reasonText.trim().length > 0) {
          submitReason();
        }
      }
    },
    [rating, selectedChips, reasonText, submitReason],
  );

  // --- Impression instrumentation: fire once when the icons first appear. ---
  const rootRef = useRef<HTMLDivElement>(null);
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    const node = rootRef.current;
    if (!node || impressionFiredRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && !impressionFiredRef.current) {
            impressionFiredRef.current = true;
            pushGtmEvent({ event: CustomGtmEvent.CHAT_FEEDBACK_IMPRESSION });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [pushGtmEvent]);

  // --- PLT-7846: hand a 👎 over to a review request, for organisations that can send one. ---
  const runtime = useAssistantRuntime();
  const canAskForReview = useQuery(reviewRequestAvailabilityQuery).data?.available === true;
  const askForReview = useCallback(() => {
    if (isRunning) return;
    // Keep the rating and its reason as they are: asking for a review adds to the rating, it does
    // not replace it.
    if (selectedChips.length > 0 || reasonText.trim().length > 0) submitReason();
    setReasonOpen(false);
    pushGtmEvent({ event: CustomGtmEvent.CHAT_REVIEW_REQUEST_FROM_FEEDBACK });
    const reasons = selectedChips
      .map(id => CHAT_FEEDBACK_REASON_CHIPS.find(chip => chip.id === id)?.label)
      .filter((label): label is string => label !== undefined);
    const detail = [reasons.join(', '), reasonText.trim()].filter(part => part.length > 0).join('. ');
    composerState.pendingSource = 'followup_suggestion';
    runtime.thread.append(
      detail.length > 0
        ? `Please ask Upright to review your last answer. What looks wrong: ${detail}`
        : 'Please ask Upright to review your last answer.',
    );
  }, [isRunning, selectedChips, reasonText, submitReason, pushGtmEvent, runtime]);

  // While the last reply is still streaming, render nothing — the control
  // appears once the answer is complete and rateable.
  if (isLast && isRunning) return null;

  return (
    <div className={clsx('upright-chat-feedback', isLast && 'upright-chat-feedback--latest')} ref={rootRef}>
      {/* Copy keeps assistant-ui's conventional placement, left of the vote pair.
          `ActionBarPrimitive.Copy asChild` wires the copy-to-clipboard behaviour
          onto our own ghost button and stamps `data-copied` on it while the
          "just copied" window is open; CSS swaps the copy glyph for the check. */}
      <Tooltip text="Copy" delay={TOOLTIP_OPEN_DELAY_MS}>
        <ActionBarPrimitive.Copy asChild>
          <button type="button" className="upright-chat-feedback-btn upright-chat-feedback-btn--copy" aria-label="Copy">
            <CopyIcon className="upright-chat-feedback-copy-icon" />
            <CheckIcon className="upright-chat-feedback-check-icon" />
          </button>
        </ActionBarPrimitive.Copy>
      </Tooltip>

      {isLast && (
        <span className="upright-chat-feedback-label">
          <Text size="sm" height="xs" color="gray-600">
            Was this helpful?
          </Text>
        </span>
      )}

      <Tooltip text="Good response" delay={TOOLTIP_OPEN_DELAY_MS}>
        <button
          type="button"
          className="upright-chat-feedback-btn upright-chat-feedback-btn--vote"
          aria-label="Good response"
          aria-pressed={rating === 'positive'}
          data-active={rating === 'positive' ? '' : undefined}
          onClick={handlePositive}
        >
          {rating === 'positive' ? <ThumbsUpFilledIcon /> : <ThumbsUpIcon />}
        </button>
      </Tooltip>

      <Tooltip text="Bad response" delay={TOOLTIP_OPEN_DELAY_MS}>
        <button
          ref={thumbsDownRef}
          type="button"
          className="upright-chat-feedback-btn upright-chat-feedback-btn--vote"
          aria-label="Bad response"
          aria-pressed={rating === 'negative'}
          data-active={rating === 'negative' ? '' : undefined}
          onClick={handleNegative}
        >
          {rating === 'negative' ? <ThumbsDownFilledIcon /> : <ThumbsDownIcon />}
        </button>
      </Tooltip>

      <PopoverTooltip
        aria-label="Tell us what went wrong"
        triggerRef={thumbsDownRef}
        isOpen={reasonOpen}
        onOpenChange={onReasonOpenChange}
        // Auto placement: prefer below (react-aria flips to top automatically
        // when there isn't room below). "Below when there's space" keeps the
        // answer the analyst is re-reading uncovered — the reason the redesign
        // moved the popover off the answer — while the flip guarantees it stays
        // on screen near the viewport edge. The arrow CSS handles both
        // data-placement="bottom" and "top".
        placement="bottom start"
        theme="light"
        className="upright-chat-feedback-popover"
        text={
          <div className="upright-chat-feedback-reason">
            <div className="upright-chat-feedback-reason-header">
              {/* Real design-system Heading (DESIGN.md: `Heading`, gray-900, bold) rather
                  than a helper-text span — the title anchors the card, per mock #1. */}
              <Heading level={2} size="xl" weight="bold" color="gray-900">
                What went wrong?
              </Heading>
              {/* Explicit dismiss so opening the popover never feels like a commitment.
                  Routes through onReasonOpenChange like click-away/Esc: any chips/text
                  already picked are kept, and the bare 👎 always stands. */}
              <button
                type="button"
                className="upright-chat-feedback-close"
                aria-label="Close"
                onClick={() => onReasonOpenChange(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="upright-chat-feedback-reason-chips">
              {CHAT_FEEDBACK_REASON_CHIPS.map(chip => (
                <button
                  key={chip.id}
                  type="button"
                  className="upright-chat-feedback-chip"
                  data-selected={selectedChips.includes(chip.id) ? '' : undefined}
                  aria-pressed={selectedChips.includes(chip.id)}
                  onClick={() => toggleChip(chip.id)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            {/* Multi-line textarea (per mock #1): a short 3-row field invites
                the paragraph of detail analysts tend to give, rather than the
                single line the field started as. Raw <textarea> styled by
                upright-chat-theme.css — matching the popover's other raw
                elements, which are raw because it renders in a portal outside
                `.upright-chat` where design-system component chrome doesn't apply. */}
            <textarea
              className="upright-chat-feedback-reason-input"
              aria-label="Tell us more (optional)"
              maxLength={CHAT_FEEDBACK_REASON_TEXT_MAX_LENGTH}
              rows={3}
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              placeholder="Tell us more (optional)"
            />
            <div className="upright-chat-feedback-reason-actions">
              <p className="upright-chat-feedback-reason-disclosure">
                The Upright team uses ratings to improve answers.
              </p>
              <button type="button" className="upright-chat-feedback-submit" onClick={submitReason}>
                Send
              </button>
            </div>
            {/* Latest answer only: the request it sends asks about "your last answer", and the
                card is filled in from it, so on an older answer it would describe the wrong one. */}
            {canAskForReview && isLast && (
              <div className="upright-chat-feedback-review">
                <span className="upright-chat-feedback-review-prompt">Need a closer look?</span>
                <button
                  type="button"
                  className="upright-chat-feedback-review-link"
                  onClick={askForReview}
                  disabled={isRunning}
                >
                  Ask Upright to review this
                </button>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}
